import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { Button, Snackbar, Menu, MenuItem } from '@mui/material';
import ResumeEditor from '../components/builder/ResumeEditor';
import TemplateSelector from '../components/builder/TemplateSelector';
import { exportToPDF } from '../utils/exportResume';
import { useAuth } from '../contexts/AuthContext';
import { resumeApi } from '../services/api';
import { templateConfigs } from '../templates';

const BuilderContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
`;

const ToolBar = styled.div`
  padding: 1rem;
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  display: flex;
  gap: 1rem;
`;

const ResumeBuilder = () => {
  const { user } = useAuth();
  const resumeRef = useRef();
  const [template, setTemplate] = useState(null);
  const [resumeData, setResumeData] = useState({});
  const [currentResumeId, setCurrentResumeId] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [isTemplateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);

  // 加载用户的简历列表
  useEffect(() => {
    const loadResumes = async () => {
      try {
        const response = await resumeApi.list();
        setResumes(response);
        if (response.length > 0) {
          setCurrentResumeId(response[0]._id);
          setResumeData(response[0].data);
          // 从模板配置中找到对应的模板
          const templateConfig = templateConfigs.find(t => t.id === response[0].templateId);
          setTemplate(templateConfig || templateConfigs[0]);
        } else {
          // 如果没有简历，设置默认模板
          setTemplate(templateConfigs[0]);
        }
      } catch (error) {
        console.error('加载简历失败:', error);
        setMessage('加载简历失败');
      }
    };
    loadResumes();
  }, []);

  const handleSave = async (data) => {
    try {
      // 如果是高级模板且用户不是会员，显示升级提示
      if (template?.isPremium && user?.subscription !== 'premium') {
        setMessage('需要升级到会员才能保存此模板');
        return;
      }
      let response;
      if (currentResumeId) {
        response = await resumeApi.update(currentResumeId, {
          templateId: template.id,
          data,
          title: data.personalInfo?.name || '未命名简历'
        });
      } else {
        response = await resumeApi.save({
          templateId: template.id,
          data,
          title: data.personalInfo?.name || '未命名简历'
        });
        setCurrentResumeId(response._id);
      }
      setMessage('简历保存成功');
      
      // 更新简历列表
      const updatedResumes = await resumeApi.list();
      setResumes(updatedResumes);
     } catch (error) {
      // 处理 403 权限错误
      if (error.response?.status === 403) {
        setMessage(error.response.data.message);
        return;
      }
      setMessage('保存失败');
    }
  };

  const handleResumeSelect = async (resumeId) => {
    try {
      const resume = await resumeApi.get(resumeId);
      setCurrentResumeId(resumeId);
      setResumeData(resume.data);
      const templateConfig = templateConfigs.find(t => t.id === resume.templateId);
      setTemplate(templateConfig || templateConfigs[0]);
      setMenuAnchor(null);
    } catch (error) {
      console.error('加载失败:', error);
      setMessage('加载简历失败');
    }
  };

  const handleExport = async () => {
    try {
      await exportToPDF(resumeRef);
      setMessage('简历导出成功');
    } catch (error) {
      setMessage('导出失败，请重试');
    }
  };

  return (
    <BuilderContainer>
      <ToolBar>
        <Button
          variant="contained"
          onClick={() => setTemplateSelectorOpen(true)}
        >
          选择模板
        </Button>
        <Button
          variant="contained"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={user?.subscription !== 'premium' && resumes.length >= 1}
        >
          选择简历
        </Button>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
        >
          {resumes.map((resume) => (
            <MenuItem
              key={resume._id}
              onClick={() => handleResumeSelect(resume._id)}
              selected={resume._id === currentResumeId}
            >
              {resume.title}
            </MenuItem>
          ))}
          {user?.subscription === 'premium' && (
            <MenuItem onClick={() => {
              setCurrentResumeId(null);
              setResumeData({});
              setMenuAnchor(null);
            }}>
              创建新简历
            </MenuItem>
          )}
        </Menu>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleExport}
        >
          导出PDF
        </Button>
      </ToolBar>

      {template ? (
        <ResumeEditor
          ref={resumeRef}
          template={template}
          initialData={resumeData}
          onSave={handleSave}
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          请选择一个简历模板开始编辑
        </div>
      )}

      <TemplateSelector
        open={isTemplateSelectorOpen}
        onClose={() => setTemplateSelectorOpen(false)}
        onSelect={setTemplate}
        userSubscription={user?.subscription}
      />

      <Snackbar
        open={!!message}
        autoHideDuration={3000}
        onClose={() => setMessage('')}
        message={message}
      />
    </BuilderContainer>
  );
};

export default ResumeBuilder; 